import {AppLocale, pickByLocale} from 'compob2b-libs';

/**
 * Типизированный словарь статических UI-строк витрины (кнопки, плейсхолдеры,
 * заголовки, подписи, пустые состояния, тексты ошибок, aria-labels).
 *
 * Контент (значения характеристик, имена категорий и шаблонных характеристик)
 * приходит уже локализованным с бэка по `Accept-Language` и здесь НЕ дублируется.
 *
 * `satisfies Record<AppLocale, ShowcaseMessages>` заставляет компилятор требовать
 * перевод КАЖДОГО ключа для КАЖДОГО языка: добавили локаль в `AppLocale`, но забыли
 * строку — ошибка сборки. Добавить язык = один типизированный блок (позже — файл).
 */
export interface ShowcaseMessages {
  /** Часто переиспользуемые подписи (заголовки таблиц, общие слова). */
  common: {
    catalog: string;
    article: string;       // заголовок столбца «Артикул»
    articleLabel: string;  // строка «Артикул:» в карточке
    photo: string;
    name: string;
    brand: string;
    documents: string;
    exportPhoto: string;
    gridView: string;      // aria «Сетка»
    listView: string;      // aria «Список»
    tileView: string;      // aria «Плитка»
  };
  /** Подписи полей блока «Свойства товара» (значения берутся из ProductCustomer). */
  staticFeature: {
    article: string;
    barcode: string;
    brand: string;
    manufacturer: string;
    series: string;
    category: string;
    width: string;
    height: string;
    length: string;
    weight: string;
  };
  filter: {
    /** Override названия секции фильтра `templateId` (BE отдаёт legacy «Шаблон»). */
    category: string;
    title: string;
    clear: string;
    clearAll: string;
    close: string;         // aria
    empty: string;
  };
  header: {
    searchPlaceholder: string;
    openSubcats: string;   // aria-префикс «Открыть подкатегории: »
    noSubcats: string;
    chipDiscount: string;
    chipAction: string;
    chipInStock: string;
  };
  catalog: {
    found: string;
    filters: string;       // мобильная кнопка «Фильтры»
    exportProducts: string;
    select: string;
    selected: string;
    selectAll: string;
    cancel: string;
    tags: string;          // aria
    nothingFound: string;
    nothingFoundHint: string;
    pages: string;         // aria
    prev: string;          // aria
    next: string;          // aria
    downloading: string;
    bulkLimit: (limit: number, selected: number) => string;
    errEmptySelection: string;
    errInvalidFormat: string;
    errTooManyItems: (limit: number) => string;
    errDownloadFailed: string;
  };
  product: {
    exportProduct: string;
    prevPhoto: string;     // aria
    nextPhoto: string;     // aria
    photoN: (n: number) => string; // aria
    variants: string;
    variantsView: string;  // aria
    tabSpecs: string;
    tabDesc: string;
    tabAnalogs: string;
    tabRelated: string;
    propsTitle: string;
    featuresEmpty: string;
    descEmpty: string;
    analogsEmpty: string;
    relatedEmpty: string;
    downloadAllDocs: string;
    docsEmpty: string;
    notFound: string;
    toCatalog: string;
  };
  card: {
    features: string;      // «Особенности:»
  };
  notFound: {
    title: string;
    desc: string;
  };
  /**
   * Единицы размера файла по возрастанию: [байты, КБ, МБ, ГБ, ТБ]. Фиксированный
   * 5-кортеж (не `string[]`) — чтобы `satisfies` ловил неверную длину на этапе
   * компиляции: `documentSize` индексирует массив позиционно (units[0]…units[4]).
   */
  units: readonly [string, string, string, string, string];
}

const MESSAGES = {
  ru: {
    common: {
      catalog: 'Каталог',
      article: 'Артикул',
      articleLabel: 'Артикул:',
      photo: 'Фото',
      name: 'Наименование',
      brand: 'Бренд',
      documents: 'Документы',
      exportPhoto: 'Экспорт фото',
      gridView: 'Сетка',
      listView: 'Список',
      tileView: 'Плитка',
    },
    staticFeature: {
      article: 'Артикул',
      barcode: 'Штрихкод',
      brand: 'Бренд',
      manufacturer: 'Производитель',
      series: 'Серия',
      category: 'Категория',
      width: 'Ширина',
      height: 'Высота',
      length: 'Длина',
      weight: 'Вес',
    },
    filter: {
      category: 'Категория товаров',
      title: 'Фильтры',
      clear: 'Сбросить',
      clearAll: 'Сбросить фильтры',
      close: 'Закрыть фильтры',
      empty: 'Нет доступных фильтров',
    },
    header: {
      searchPlaceholder: 'Поиск товара',
      openSubcats: 'Открыть подкатегории: ',
      noSubcats: 'Нет подкатегорий',
      chipDiscount: 'Уценка',
      chipAction: 'Акция',
      chipInStock: 'В наличии',
    },
    catalog: {
      found: 'Найдено',
      filters: 'Фильтры',
      exportProducts: 'Экспорт товаров',
      select: 'Выбрать',
      selected: 'Выбрано:',
      selectAll: 'Все',
      cancel: 'Отменить',
      tags: 'Теги',
      nothingFound: 'Ничего не найдено',
      nothingFoundHint: 'Попробуйте изменить фильтры или выбрать другую категорию.',
      pages: 'Страницы',
      prev: 'Назад',
      next: 'Вперёд',
      downloading: 'Скачиваем…',
      bulkLimit: (limit: number, selected: number) => `Лимит: ${limit} (выбрано ${selected})`,
      errEmptySelection: 'Выберите хотя бы один товар, доступный в этой витрине.',
      errInvalidFormat: 'Неподдерживаемый формат экспорта.',
      errTooManyItems: (limit: number) => `Слишком много товаров — выберите не больше ${limit}.`,
      errDownloadFailed: 'Не удалось скачать архив. Попробуйте ещё раз.',
    },
    product: {
      exportProduct: 'Экспорт товара',
      prevPhoto: 'Предыдущее фото',
      nextPhoto: 'Следующее фото',
      photoN: (n: number) => `Фото ${n}`,
      variants: 'Варианты товара',
      variantsView: 'Вид отображения вариантов',
      tabSpecs: 'Характеристики',
      tabDesc: 'Описание',
      tabAnalogs: 'Аналоги',
      tabRelated: 'Сопутствующие',
      propsTitle: 'Свойства товара',
      featuresEmpty: 'Характеристики не заполнены.',
      descEmpty: 'Описание не заполнено.',
      analogsEmpty: 'Аналоги не указаны.',
      relatedEmpty: 'Сопутствующие товары не указаны.',
      downloadAllDocs: 'Скачать все документы',
      docsEmpty: 'Документы не приложены.',
      notFound: 'Товар не найден',
      toCatalog: 'К каталогу',
    },
    card: {features: 'Особенности:'},
    notFound: {
      title: 'Страница не найдена',
      desc: 'Возможно, она была удалена, переименована или ссылка содержит ошибку.',
    },
    units: ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ'],
  },
  en: {
    common: {
      catalog: 'Catalog',
      article: 'Article',
      articleLabel: 'Article:',
      photo: 'Photo',
      name: 'Name',
      brand: 'Brand',
      documents: 'Documents',
      exportPhoto: 'Export photos',
      gridView: 'Grid',
      listView: 'List',
      tileView: 'Tiles',
    },
    staticFeature: {
      article: 'Article',
      barcode: 'Barcode',
      brand: 'Brand',
      manufacturer: 'Manufacturer',
      series: 'Series',
      category: 'Category',
      width: 'Width',
      height: 'Height',
      length: 'Length',
      weight: 'Weight',
    },
    filter: {
      category: 'Product Category',
      title: 'Filters',
      clear: 'Reset',
      clearAll: 'Reset filters',
      close: 'Close filters',
      empty: 'No filters available',
    },
    header: {
      searchPlaceholder: 'Search products',
      openSubcats: 'Open subcategories: ',
      noSubcats: 'No subcategories',
      chipDiscount: 'Markdown',
      chipAction: 'Promo',
      chipInStock: 'In stock',
    },
    catalog: {
      found: 'Found',
      filters: 'Filters',
      exportProducts: 'Export products',
      select: 'Select',
      selected: 'Selected:',
      selectAll: 'All',
      cancel: 'Cancel',
      tags: 'Tags',
      nothingFound: 'Nothing found',
      nothingFoundHint: 'Try changing the filters or choosing another category.',
      pages: 'Pages',
      prev: 'Previous',
      next: 'Next',
      downloading: 'Downloading…',
      bulkLimit: (limit: number, selected: number) => `Limit: ${limit} (${selected} selected)`,
      errEmptySelection: 'Select at least one product available in this showcase.',
      errInvalidFormat: 'Unsupported export format.',
      errTooManyItems: (limit: number) => `Too many products — select no more than ${limit}.`,
      errDownloadFailed: 'Couldn’t download the archive. Please try again.',
    },
    product: {
      exportProduct: 'Export product',
      prevPhoto: 'Previous photo',
      nextPhoto: 'Next photo',
      photoN: (n: number) => `Photo ${n}`,
      variants: 'Product variants',
      variantsView: 'Variants display mode',
      tabSpecs: 'Specifications',
      tabDesc: 'Description',
      tabAnalogs: 'Analogs',
      tabRelated: 'Related',
      propsTitle: 'Product properties',
      featuresEmpty: 'No specifications provided.',
      descEmpty: 'No description provided.',
      analogsEmpty: 'No analogs specified.',
      relatedEmpty: 'No related products specified.',
      downloadAllDocs: 'Download all documents',
      docsEmpty: 'No documents attached.',
      notFound: 'Product not found',
      toCatalog: 'To catalog',
    },
    card: {features: 'Features:'},
    notFound: {
      title: 'Page not found',
      desc: 'It may have been deleted, renamed, or the link contains an error.',
    },
    units: ['B', 'KB', 'MB', 'GB', 'TB'],
  },
} satisfies Record<AppLocale, ShowcaseMessages>;

/** Подписи витрины для указанной локали (фолбэк на дефолтную через pickByLocale). */
export function showcaseMessages(locale: AppLocale): ShowcaseMessages {
  return pickByLocale(MESSAGES, locale);
}
